import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  AlertTriangle, CheckCircle, XCircle, Package, 
  TrendingUp, TrendingDown, DollarSign, BarChart3,
  AlertOctagon, Loader2, RefreshCw, Eye, EyeOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const SEVERITY_COLORS = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-blue-100 text-blue-800 border-blue-200'
};

const ALERT_TYPE_ICONS = {
  out_of_stock: AlertOctagon,
  low_stock: AlertTriangle,
  overstock: Package
};

export default function InventoryAlerts() {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load alerts
      const alertsRes = await api.get(`/inventory-intel/alerts?acknowledged=${activeTab === 'acknowledged'}`);
      setAlerts(alertsRes.data.alerts || []);
      
      // Load metrics
      const metricsRes = await api.get('/inventory-intel/metrics');
      setMetrics(metricsRes.data);
    } catch (error) {
      toast.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  const generateAlerts = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/inventory-intel/alerts/generate');
      toast.success(`Generated ${res.data.alerts_generated} new alerts`);
      loadData();
    } catch (error) {
      toast.error('Failed to generate alerts');
    } finally {
      setGenerating(false);
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      await api.post(`/inventory-intel/alerts/${alertId}/acknowledge`);
      toast.success('Alert acknowledged');
      loadData();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Inventory Intelligence</h1>
          <p className="text-gray-600">Smart alerts and reorder suggestions</p>
        </div>
        <Button onClick={generateAlerts} disabled={generating}>
          {generating ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Generate Alerts
        </Button>
      </div>

      {/* Metrics Cards */}
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Products</p>
                  <p className="text-2xl font-bold">{metrics.total_products}</p>
                </div>
                <Package className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Inventory Value</p>
                  <p className="text-2xl font-bold">
                    ${metrics.total_inventory_value?.toLocaleString()}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Out of Stock</p>
                  <p className="text-2xl font-bold text-red-600">
                    {metrics.out_of_stock_count}
                  </p>
                </div>
                <AlertOctagon className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Low Stock</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {metrics.low_stock_count}
                  </p>
                </div>
                <AlertTriangle className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Alerts Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="active">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Active Alerts ({alerts.filter(a => !a.acknowledged).length})
          </TabsTrigger>
          <TabsTrigger value="acknowledged">
            <CheckCircle className="w-4 h-4 mr-2" />
            Acknowledged
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          <AlertsTable 
            alerts={alerts.filter(a => !a.acknowledged)}
            onAcknowledge={acknowledgeAlert}
            showActions
          />
        </TabsContent>

        <TabsContent value="acknowledged">
          <AlertsTable 
            alerts={alerts.filter(a => a.acknowledged)}
            showActions={false}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AlertsTable({ alerts, onAcknowledge, showActions }) {
  if (alerts.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <p className="text-gray-600">No alerts to display</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Product</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Alert Type</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Severity</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Stock</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Suggested Qty</th>
            {showActions && <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Action</th>}
          </tr>
        </thead>
        <tbody className="divide-y">
          {alerts.map((alert) => {
            const Icon = ALERT_TYPE_ICONS[alert.alert_type] || AlertTriangle;
            
            return (
              <tr key={alert.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div>
                    <p className="font-medium">{alert.product_name}</p>
                    <p className="text-sm text-gray-500">{alert.sku}</p>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    <span className="capitalize">{alert.alert_type.replace('_', ' ')}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Badge className={SEVERITY_COLORS[alert.severity]}>
                    {alert.severity}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <span className={alert.current_quantity === 0 ? 'text-red-600 font-bold' : ''}>
                    {alert.current_quantity}
                  </span>
                  <span className="text-gray-400 text-sm"> / {alert.threshold_quantity}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="font-medium text-blue-600">
                    +{alert.suggested_reorder_quantity}
                  </span>
                </td>
                {showActions && (
                  <td className="px-4 py-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onAcknowledge(alert.id)}
                    >
                      <EyeOff className="w-4 h-4 mr-1" />
                      Acknowledge
                    </Button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
